import { useState, useEffect } from 'react';
import { X, Envelope, Warning, Gavel, Download, SpinnerGap, WhatsappLogo, PaperPlaneRight } from '@phosphor-icons/react';
import API, { fmt } from '@/lib/api';

/**
 * BetalingsherinneringModal
 * ─────────────────────────
 * Modal om een gecombineerde betalingsherinnering aan te maken voor 1 klant
 * met meerdere openstaande facturen, in 3 escalatie-niveaus.
 * Ondersteunt 3 verzendkanalen:
 *   • PDF downloaden
 *   • Verstuur per e-mail (SendGrid)
 *   • Delen via WhatsApp Business (public link naar tijdelijke PDF)
 */
export default function BetalingsherinneringModal({ klantId, klantNaam, klantEmail, klantGsm, klantTaal, open, onClose, preselectIds = [], defaultNiveau = 1 }) {
  const [facturen, setFacturen] = useState([]);
  const [selected, setSelected] = useState(new Set(preselectIds));
  const [niveau, setNiveau] = useState(defaultNiveau);
  const [taal, setTaal] = useState(klantTaal || 'nl');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');   // '' | 'pdf' | 'email' | 'whatsapp'
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  // Email sub-modus
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailBericht, setEmailBericht] = useState('');

  const preselectKey = preselectIds.join(',');

  useEffect(() => {
    if (!open || !klantId) return;
    setLoading(true);
    setErr(''); setOk('');
    setTaal(klantTaal || 'nl');
    setNiveau(defaultNiveau);
    API.get(`/klanten/${klantId}/openstaande-facturen`)
      .then(r => {
        setFacturen(r.data || []);
        if (preselectIds.length === 0) {
          setSelected(new Set((r.data || []).map(f => f.id)));
        } else {
          setSelected(new Set(preselectIds));
        }
      })
      .catch(e => setErr(e?.response?.data?.detail || 'Kon openstaande facturen niet ophalen'))
      .finally(() => setLoading(false));
    setEmailTo(klantEmail || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, klantId, klantEmail, klantTaal, preselectKey, defaultNiveau]);

  if (!open) return null;

  const TALEN = [
    { id: 'nl', label: 'NL' },
    { id: 'fr', label: 'FR' },
    { id: 'en', label: 'EN' },
  ];

  const toggle = (id) => {
    const nw = new Set(selected);
    if (nw.has(id)) nw.delete(id); else nw.add(id);
    setSelected(nw);
  };
  const toggleAll = () => {
    if (selected.size === facturen.length) setSelected(new Set());
    else setSelected(new Set(facturen.map(f => f.id)));
  };

  const totaalOpenstaand = facturen
    .filter(f => selected.has(f.id))
    .reduce((s, f) => s + Number(f.openstaand || 0), 0);

  const NIVEAUS = [
    { id: 1, label: '1e Herinnering', sub: 'Vriendelijk', kleur: '#002FA7', icon: Envelope, tekst: 'Standaard herinnering, vriendelijke toon. Termijn: 7 werkdagen.' },
    { id: 2, label: '2e Herinnering', sub: 'Strenger', kleur: '#EA580C', icon: Warning, tekst: 'Dringende toon, waarschuwt voor juridische stappen. Termijn: 5 werkdagen.' },
    { id: 3, label: 'Ingebrekestelling', sub: 'Deurwaarder', kleur: '#B91C1C', icon: Gavel, tekst: 'Laatste aanmaning. Formele ingebrekestelling (art. 1139 BW). Termijn: 8 kalenderdagen. Kondigt overdracht aan gerechtsdeurwaarder aan.' },
  ];
  const gekozenNiveau = NIVEAUS.find(n => n.id === niveau) || NIVEAUS[0];

  const geldigeSelectie = () => {
    if (selected.size === 0) { setErr('Selecteer minstens één factuur'); return false; }
    setErr('');
    return true;
  };

  const doenPdf = async () => {
    if (!geldigeSelectie()) return;
    setBusy('pdf'); setOk('');
    try {
      const resp = await API.post('/betalingsherinneringen/pdf', {
        klant_id: klantId, factuur_ids: Array.from(selected), niveau, taal,
      }, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.target = '_blank'; link.rel = 'noopener';
      link.download = `Betalingsherinnering_N${niveau}_${(klantNaam || 'klant').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      setOk('PDF gegenereerd — kijk in je downloads-map');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'PDF-generatie mislukt');
    } finally { setBusy(''); }
  };

  const doenEmail = async () => {
    if (!geldigeSelectie()) return;
    if (!emailTo || !emailTo.includes('@')) { setErr('Vul een geldig e-mailadres in'); return; }
    setBusy('email'); setOk('');
    try {
      const resp = await API.post('/betalingsherinneringen/verzenden', {
        klant_id: klantId, factuur_ids: Array.from(selected), niveau, taal,
        to: emailTo, bericht: emailBericht,
      });
      setOk(`✓ E-mail verstuurd naar ${resp.data.verzonden_aan}`);
      setEmailFormOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'E-mail versturen mislukt');
    } finally { setBusy(''); }
  };

  const doenWhatsApp = async () => {
    if (!geldigeSelectie()) return;
    setBusy('whatsapp'); setOk('');
    try {
      const resp = await API.post('/betalingsherinneringen/whatsapp-link', {
        klant_id: klantId, factuur_ids: Array.from(selected), niveau, taal,
      });
      // Open WhatsApp in nieuw tabblad
      window.open(resp.data.whatsapp_url, '_blank', 'noopener');
      setOk(`✓ WhatsApp geopend — PDF link vervalt over ${Math.floor(resp.data.expires_in_seconds / 60)} min`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'WhatsApp-link genereren mislukt');
    } finally { setBusy(''); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bh-modal">
      <div className="bg-white shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ background: `linear-gradient(90deg, ${gekozenNiveau.kleur}0D, transparent)` }}>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: gekozenNiveau.kleur }}>Betalingsherinnering</div>
            <h2 className="text-lg font-black text-gray-900" data-testid="bh-modal-title">{klantNaam || 'Klant'}</h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex border border-gray-200 rounded overflow-hidden" data-testid="bh-taal-picker">
              {TALEN.map(t => (
                <button
                  key={t.id}
                  onClick={() => setTaal(t.id)}
                  className={`px-2.5 py-1.5 text-[11px] font-bold transition-colors ${taal === t.id ? 'text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                  style={taal === t.id ? { background: gekozenNiveau.kleur } : {}}
                  data-testid={`bh-taal-${t.id}`}
                  title={t.id === 'nl' ? 'Nederlands' : t.id === 'fr' ? 'Français' : 'English'}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="bh-close"><X size={22} /></button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Niveau */}
          <div>
            <div className="text-[10px] font-black uppercase tracking-wider text-gray-500 mb-2">1. Kies het niveau</div>
            <div className="grid grid-cols-3 gap-2">
              {NIVEAUS.map(n => {
                const Ic = n.icon;
                const actief = niveau === n.id;
                return (
                  <button
                    key={n.id}
                    onClick={() => setNiveau(n.id)}
                    className={`p-3 border-2 transition-all text-left ${actief ? 'shadow-md' : 'hover:shadow-sm opacity-70 hover:opacity-100'}`}
                    style={{ borderColor: actief ? n.kleur : '#E5E7EB', background: actief ? `${n.kleur}0F` : '#FFF' }}
                    data-testid={`bh-niveau-${n.id}`}
                  >
                    <Ic size={20} weight={actief ? 'fill' : 'regular'} style={{ color: n.kleur }} />
                    <div className="mt-1 text-sm font-bold" style={{ color: actief ? n.kleur : '#374151' }}>{n.label}</div>
                    <div className="text-[10px] font-medium" style={{ color: n.kleur, opacity: 0.8 }}>{n.sub}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] text-gray-500 italic">{gekozenNiveau.tekst}</div>
          </div>

          {/* Facturen */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-black uppercase tracking-wider text-gray-500">2. Selecteer openstaande facturen</div>
              {facturen.length > 0 && (
                <button onClick={toggleAll} className="text-[10px] font-bold text-[#002FA7] hover:underline" data-testid="bh-toggle-all">
                  {selected.size === facturen.length ? 'Deselecteer alles' : 'Selecteer alles'}
                </button>
              )}
            </div>
            {loading && <div className="text-center py-6 text-gray-400 text-xs"><SpinnerGap size={20} className="animate-spin inline mr-2" />Laden…</div>}
            {!loading && facturen.length === 0 && (
              <div className="text-center py-6 border border-dashed border-gray-200 rounded text-gray-400 text-xs">Geen openstaande facturen voor deze klant.</div>
            )}
            {!loading && facturen.length > 0 && (
              <div className="border border-gray-200 rounded max-h-56 overflow-y-auto divide-y divide-gray-100">
                {facturen.map(f => {
                  const sel = selected.has(f.id);
                  const isOverdue = f.verval && new Date(f.verval) < new Date();
                  return (
                    <label key={f.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 ${sel ? 'bg-blue-50' : ''}`} data-testid={`bh-factuur-row-${f.id}`}>
                      <input type="checkbox" checked={sel} onChange={() => toggle(f.id)} className="w-4 h-4" data-testid={`bh-factuur-check-${f.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-[#002FA7] text-xs">{f.nummer || f.doc_nr || f.id}</span>
                          {isOverdue && <span className="text-[9px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">VERVALLEN</span>}
                        </div>
                        <div className="text-[10px] text-gray-500 mt-0.5">Datum: {f.datum || '—'} · Verval: {f.verval || '—'}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Totaal {fmt(f.totaal)}</div>
                        <div className="text-sm font-bold text-red-600">{fmt(f.openstaand)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Totaal + 3 actie-knoppen */}
          {facturen.length > 0 && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Totaal openstaand</div>
                  <div className="text-2xl font-black" style={{ color: gekozenNiveau.kleur }} data-testid="bh-totaal-openstaand">{fmt(totaalOpenstaand)}</div>
                  <div className="text-[10px] text-gray-400">{selected.size} van {facturen.length} facturen</div>
                </div>
              </div>

              {/* Email sub-formulier — schuift in wanneer geopend */}
              {emailFormOpen && (
                <div className="mb-3 p-3 border border-blue-200 bg-blue-50 space-y-2" data-testid="bh-email-form">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-blue-900">E-mailadres</label>
                    <input
                      type="email"
                      value={emailTo}
                      onChange={e => setEmailTo(e.target.value)}
                      placeholder="klant@voorbeeld.be"
                      className="w-full mt-1 px-2 py-1.5 border border-blue-200 text-sm"
                      data-testid="bh-email-to"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-blue-900">Extra bericht (optioneel)</label>
                    <textarea
                      value={emailBericht}
                      onChange={e => setEmailBericht(e.target.value)}
                      placeholder="Persoonlijk bericht dat in de e-mail komt vóór de PDF-bijlage…"
                      rows={2}
                      className="w-full mt-1 px-2 py-1.5 border border-blue-200 text-xs"
                      data-testid="bh-email-bericht"
                    />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setEmailFormOpen(false)} className="px-3 py-1 text-xs text-gray-600 hover:text-gray-900" data-testid="bh-email-cancel">Annuleren</button>
                    <button
                      onClick={doenEmail}
                      disabled={busy === 'email'}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                      data-testid="bh-email-verstuur"
                    >
                      {busy === 'email' ? <SpinnerGap size={14} className="animate-spin" /> : <PaperPlaneRight size={14} weight="bold" />}
                      {busy === 'email' ? 'Verzenden…' : 'Verstuur nu'}
                    </button>
                  </div>
                </div>
              )}

              {/* 3-knoppen rij */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={doenPdf}
                  disabled={busy || selected.size === 0}
                  className="py-3 px-3 font-bold text-white flex items-center justify-center gap-2 shadow hover:shadow-md transition-shadow disabled:opacity-50"
                  style={{ background: gekozenNiveau.kleur }}
                  data-testid="bh-genereer-pdf"
                >
                  {busy === 'pdf' ? <SpinnerGap size={16} className="animate-spin" /> : <Download size={16} weight="bold" />}
                  <span className="text-xs">PDF</span>
                </button>
                <button
                  onClick={() => setEmailFormOpen(v => !v)}
                  disabled={busy || selected.size === 0}
                  className="py-3 px-3 font-bold text-white flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] shadow hover:shadow-md transition-shadow disabled:opacity-50"
                  data-testid="bh-open-email"
                >
                  <Envelope size={16} weight="bold" />
                  <span className="text-xs">{emailFormOpen ? 'Sluit e-mail' : 'E-mail'}</span>
                </button>
                <button
                  onClick={doenWhatsApp}
                  disabled={busy || selected.size === 0}
                  className="py-3 px-3 font-bold text-white flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5B] shadow hover:shadow-md transition-shadow disabled:opacity-50"
                  data-testid="bh-whatsapp"
                  title={klantGsm ? `Naar ${klantGsm}` : 'Kies contact in WhatsApp'}
                >
                  {busy === 'whatsapp' ? <SpinnerGap size={16} className="animate-spin" /> : <WhatsappLogo size={16} weight="fill" />}
                  <span className="text-xs">WhatsApp</span>
                </button>
              </div>
            </div>
          )}

          {ok && <div className="p-3 bg-green-50 border border-green-200 text-green-800 text-xs rounded" data-testid="bh-ok">{ok}</div>}
          {err && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded" data-testid="bh-error">⚠ {err}</div>}
        </div>
      </div>
    </div>
  );
}
